import { extendZodWithOpenApi } from '@anatine/zod-openapi'
import { z } from 'zod'
import { PartnerTier } from './types'
import { AddressState } from '../order'
import { DateTimeSchema } from '../common'

extendZodWithOpenApi(z)

export const TenantType = z.enum([
  'LykaStaff',
  'Partner',
  'FulfillmentStaff',
  'D2cCustomer',
  'Guest',
  'TygaCustomer',
  'TygaLead',
  'LykaLead',
  'CustomerCare',
  // Partners
  'Breeder',
  'Vet',
  'Influencer',
  'OtherPartnerBusiness',
  'Affiliate',
  'Research',
  'Rescue',
  'BehaviouristTrainer',
])
export type TenantType = z.infer<typeof TenantType>

export const PartnerTenantType = TenantType.extract([
  'Breeder',
  'Vet',
  'Influencer',
  'OtherPartnerBusiness',
  'Affiliate',
  'Research',
  'Rescue',
  'BehaviouristTrainer',
])
export type PartnerTenantType = z.infer<typeof PartnerTenantType>

export const HowDidYouHearAboutUs = z.object({
  id: z.string().uuid(),
  primaryReason: z.string().min(1),
  secondaryReason: z.string().min(1).optional(),
  otherReason: z.string().min(1).optional(),
}).strict()
export type HowDidYouHearAboutUs = z.infer<typeof HowDidYouHearAboutUs>

/** Shape of the `users.metadata` JSONB column in the auth service. */
export const UserMetadata = z.object({
  oktaUserId: z.string().min(1).optional(),
  howDidYouHearAboutUs: HowDidYouHearAboutUs.optional(),
}).strict()
export type UserMetadata = z.infer<typeof UserMetadata>

/**
 * `metadata` accepted by the migration endpoints. Persisted to
 * `user_id_mapping.legacy_user_id`, not to `users.metadata`.
 */
export const MigrationUserMetadata = z.object({
  legacyUserId: z.number().int().positive(),
}).strict()
export type MigrationUserMetadata = z.infer<typeof MigrationUserMetadata>

export const GetUsersResponse = z.object({
  result: z.array(
    z.object({
      cognito_user_id: z.string().uuid(),
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
      phone_number: z.string(),
      role_name: z.string(),
      tenant_type: TenantType,
      metadata: UserMetadata.optional(),
    }),
  ),
})
export type GetUsersResponse = z.infer<typeof GetUsersResponse>

export const BusinessProfile = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  businessName: z.string().nullable(),
  abn: z.string().regex(/^\d{11}$/).nullable(),
  tier: z.nativeEnum(PartnerTier).nullable(),
  discountId: z.string().uuid(),
  hubspotCompanyId: z.string().nullable(),
  deactivatedAt: DateTimeSchema.nullable(),
  deactivatedBy: z.string().uuid().nullable(),
  createdBy: z.string().uuid().nullable(),
  createdAt: DateTimeSchema,
  updatedAt: DateTimeSchema,
  deletedAt: DateTimeSchema.nullable(),
})
export type BusinessProfile = z.infer<typeof BusinessProfile>

export const CreateBusinessProfile = BusinessProfile.omit({
  id: true,
  createdBy: true,
  deactivatedAt: true,
  deactivatedBy: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export type CreateBusinessProfile = z.infer<typeof CreateBusinessProfile>

export const UpdateBusinessProfile = BusinessProfile.pick({
  businessName: true,
  abn: true,
  tier: true,
  discountId: true,
  hubspotCompanyId: true,
}).partial()
export type UpdateBusinessProfile = z.infer<typeof UpdateBusinessProfile>

export const GenericBusinessProfileParam = z.object({
  userId: z.string().uuid(),
})
export type GenericBusinessProfileParam = z.infer<typeof GenericBusinessProfileParam>

export const FilterPartnerQuery = z.object({
  tenantType: z.union([PartnerTenantType, PartnerTenantType.array()])
    .optional()
    .transform(value => (value === undefined ? PartnerTenantType.options : [value].flat())),
  offset: z.number({ coerce: true }).int().gte(0).default(0),
  limit: z.number({ coerce: true }).int().gte(1).lte(100).default(25),
})
export type FilterPartnerQuery = z.infer<typeof FilterPartnerQuery>

export const PartnerResponse = z.object({
  cognitoUserId: z.string().uuid(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  phoneNumber: z.string().nullable(),
  tenantType: PartnerTenantType,
  isActivated: z.boolean(),
  createdAt: DateTimeSchema,
  businessProfile: BusinessProfile.pick({
    id: true,
    businessName: true,
    abn: true,
    tier: true,
    discountId: true,
    hubspotCompanyId: true,
  }).nullable(),
})
export type PartnerResponse = z.infer<typeof PartnerResponse>

export const FilterPartnersResponse = z.object({
  meta: z.object({
    offset: z.number().int(),
    limit: z.number().int(),
    total: z.number().int(),
  }),
  result: PartnerResponse.array(),
})
export type FilterPartnersResponse = z.infer<typeof FilterPartnersResponse>

export const CreatePartnerAddress = z.object({
  companyName: z.string().min(1).optional(),
  address1: z.string().min(1),
  address2: z.string().min(1).optional(),
  suburb: z.string().min(1),
  state: z.nativeEnum(AddressState),
  countryCode: z.string().default('AU'),
  postcode: z.string().regex(/^\d{4}$/),
  deliveryInstructions: z.string().min(1).optional(),
}).strict()
export type CreatePartnerAddress = z.infer<typeof CreatePartnerAddress>

export const CreatePartnerRequest = z.object({
  tenantType: PartnerTenantType,
  firstName: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phoneNumber: z.string().min(1).optional(),
  businessName: z.string().min(1).optional(),
  abn: z.string().regex(/^\d{11}$/).optional(),
  hubspotCompanyId: z.string().min(1).optional(),
  address: CreatePartnerAddress,
  discountId: z.string().uuid(),
}).strict()
export type CreatePartnerRequest = z.infer<typeof CreatePartnerRequest>

export const CreatePartnerResponse = z.object({
  result: z.object({
    userId: z.string().uuid(),
    businessProfile: BusinessProfile,
  }),
})
export type CreatePartnerResponse = z.infer<typeof CreatePartnerResponse>
